import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  Award,
  BarChart3,
  BookOpen,
  ClipboardCheck,
  Bot,
  CreditCard,
  LayoutDashboard,
  Image,
  MessageSquare,
  Settings2,
  Store,
  Timer,
  WandSparkles,
  Users,
} from 'lucide-react';

import type { AdminTopLevelTab } from './adminTabs';
import { FEATURE_FLAG_KEYS, type KnownFeatureFlagKey } from './featureFlagsCatalog';

export interface AdminNavigationItem {
  tab: AdminTopLevelTab;
  title: string;
  description: string;
  url: string;
  icon: LucideIcon;
  /**
   * When set, this nav item is only shown when the given feature flag resolves
   * enabled. Fail-closed: absent/loading flag state hides the item.
   */
  requiredFeatureFlag?: KnownFeatureFlagKey;
}

const adminNavigationDefinitions: Array<Omit<AdminNavigationItem, 'url'>> = [
  {
    tab: 'overview',
    title: 'Overview',
    description: 'Platform health, support visibility, and quick admin actions.',
    icon: LayoutDashboard,
  },
  {
    tab: 'accounts',
    title: 'Accounts',
    description: 'Users, organisations, and MSP account controls.',
    icon: Users,
  },
  {
    tab: 'billing',
    title: 'Billing',
    description: 'Subscription management, plans, and billing operations.',
    icon: CreditCard,
  },
  {
    tab: 'communications',
    title: 'Communications',
    description: 'Email tools, invitations, and support notices.',
    icon: MessageSquare,
  },
  {
    tab: 'content',
    title: 'Content',
    description: 'Questionnaires, glossary content, and breach feed curation.',
    icon: BookOpen,
  },
  {
    tab: 'training',
    title: 'Assessments',
    description: 'Breach Aware question banks, certification rules, and published versions.',
    icon: ClipboardCheck,
  },
  {
    tab: 'marketplace',
    title: 'Marketplace',
    description: 'Marketplace listings, supplier profiles, and dependencies.',
    icon: Store,
  },
  {
    tab: 'certificates',
    title: 'Certificates',
    description: 'Certificate records, registry uploads, and logo assets.',
    icon: Award,
  },
  {
    tab: 'scan-engine',
    title: 'Scan Engine',
    description: 'TLS scan probe deadline hierarchy and timeout settings.',
    icon: Timer,
    requiredFeatureFlag: FEATURE_FLAG_KEYS.scanEngineSettings,
  },
  {
    tab: 'fleet-operations',
    title: 'Fleet Operations',
    description: 'Worker fleets: live queue, throughput history, rollout and scale controls.',
    icon: Activity,
    requiredFeatureFlag: FEATURE_FLAG_KEYS.scanEngineOperations,
  },
  {
    tab: 'logo-processing',
    title: 'Logo Processing',
    description: 'Canonical logo queue coverage, recent bounded runs, and attempt limits.',
    icon: Image,
    requiredFeatureFlag: FEATURE_FLAG_KEYS.canonicalLogoProcessing,
  },
  {
    tab: 'ai-agent',
    title: 'SupplySteward',
    description: 'AI agent settings, prompts, and knowledge base controls.',
    icon: Bot,
  },
  {
    tab: 'insights',
    title: 'Insights',
    description: 'Platform analytics and operational insights.',
    icon: BarChart3,
  },
  {
    tab: 'scoring',
    title: 'Scoring',
    description: 'Scoring model configuration and simulation workflows.',
    icon: WandSparkles,
  },
  {
    tab: 'platform',
    title: 'Platform',
    description: 'Platform docs, cron, API keys, API quotas, security, and feature flags.',
    icon: Settings2,
  },
];

export interface AdminNavigationOptions {
  /**
   * Map of feature-flag key -> enabled. Items declaring `requiredFeatureFlag`
   * are hidden unless their flag is explicitly true (fail-closed while the flag
   * is loading/undefined).
   */
  enabledFeatureFlags?: Partial<Record<KnownFeatureFlagKey, boolean>>;
}

export function getAdminNavigationItems(
  options: AdminNavigationOptions = {},
): AdminNavigationItem[] {
  const { enabledFeatureFlags = {} } = options;

  return adminNavigationDefinitions
    .filter((item) =>
      item.requiredFeatureFlag
        ? enabledFeatureFlags[item.requiredFeatureFlag] === true
        : true,
    )
    .map((item) => ({
      ...item,
      url: item.tab === 'overview' ? '/admin' : `/admin?tab=${item.tab}`,
    }));
}
